<template>
  <div class="flex flex-col p-3">
    <!-- Filter bar -->
    <div class="flex items-center gap-3 py-2 flex-wrap">
      <input type="text" v-model="search" placeholder="Search by BRN"
             class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary w-48"
             @keyup.enter="onSearch" />
      <select v-model="statuses"
              class="px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary"
              @change="onSearch">
        <option :value="null">-- Filter by Status --</option>
        <option value="PENDING">Pending</option>
        <option value="Success">Success</option>
        <option value="Sent">Sent</option>
        <option value="Failed">Failed</option>
      </select>
      <input type="date" v-model="startDate"
             class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
             @change="onSearch" />
      <input type="date" v-model="endDate"
             class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
             @change="onSearch" />
      <select v-model="limit"
              class="px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary"
              @change="onSearch">
        <option :value="12">12</option>
        <option :value="20">20</option>
        <option :value="50">50</option>
        <option :value="100">100</option>
      </select>
    </div>

    <!-- Table -->
    <div>
      <table>
        <thead>
        <tr>
          <th>#</th>
          <th>BRN</th>
          <th>Type</th>
          <th>Owner</th>
          <th>Retry Count</th>
          <th>Status</th>
          <th>Date</th>
          <th>Last Ran</th>
          <th>Action</th>
        </tr>
        </thead>
        <tbody>
        <template v-if="loading">
          <tr v-for="i in 6" :key="`sk-${i}`" class="animate-pulse">
            <td v-for="j in 9" :key="j">
              <div class="h-3 bg-gray-200 rounded"></div>
            </td>
          </tr>
        </template>
        <template v-else>
          <tr v-for="(action, idx) in stream" :key="idx">
            <td>{{ (currentPage - 1) * limit + idx + 1 }}</td>
            <td class="font-bold font-mono cursor-pointer hover:underline text-primary"
                @click="searchTerm(action.brn || '')">{{ action.brn }}
            </td>
            <td class="italic text-blue-600 cursor-pointer hover:underline" @click="searchTerm(action.type || '')">
              {{ action.type }}
            </td>
            <td>{{ action.brn }}</td>
            <td class="text-center">{{ action.retries }}</td>
            <td><span :class="statusPill(action.status)">{{ action.status?.toLowerCase() }}</span></td>
            <td class="text-xs">{{ dateFormat(action.createdAt) }}</td>
            <td class="text-xs">{{ dateFormat(action.updatedAt) }}</td>
            <td>
              <div class="flex gap-1">
                <button
                  v-if="action.status === 'FAILED' || action.status === 'PAUSED' || action.status === 'IN_PROGRESS'"
                  class="btn-sm-danger" @click="retry(action, idx)">
                  <i class="fa fa-redo mr-1"></i> Retry
                </button>
                <button class="btn-sm-primary" @click="view(action)">
                  <i class="fa fa-eye mr-1"></i> View
                </button>
              </div>
            </td>
          </tr>
        </template>
        <tr v-if="!loading && stream.length === 0">
          <td colspan="9" class="text-center py-6 text-gray-400 text-sm">No results found</td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="pagination.totalPages > 1"
         class="flex items-center justify-between px-1 py-3 border-t border-gray-100 mt-2">
      <p class="text-xs text-gray-500">Page <span class="font-semibold text-gray-700">{{ currentPage }}</span> of <span
        class="font-semibold text-gray-700">{{ pagination.totalPages }}</span></p>
      <div class="flex gap-2">
        <button class="btn-sm-primary" @click="previous" :disabled="currentPage === 1">
          <i class="fa fa-chevron-left mr-1"></i> Prev
        </button>
        <button class="btn-sm-primary" @click="next" :disabled="currentPage === pagination.totalPages">
          Next <i class="fa fa-chevron-right ml-1"></i>
        </button>
      </div>
    </div>
  </div>

  <!-- Detail Modal -->
  <AppModal v-model="showDetails" size="xl" title="Process Details" hide-footer>
    <div class="space-y-3 text-sm">
      <p><b>BRN:</b> {{ process.brn }}</p>
      <p><b>Type:</b> {{ process.type }}</p>
      <p><b>Retries:</b> {{ process.retries }}</p>
      <p><b>Date:</b> {{ dateFormat(process.createdAt) }}</p>
      <div v-if="process.payload">
        <p class="font-bold mt-3">Request</p>
        <pre class="text-green-700 text-xs bg-green-50 border border-green-200 p-2 rounded overflow-auto max-h-96">{{
            formatPayload(process.payload)
          }}</pre>
      </div>
      <div v-if="process.response">
        <p class="font-bold mt-3">Response</p>
        <pre class="text-red-700 text-xs bg-red-50 border border-red-200 p-2 rounded overflow-auto max-h-96 whitespace-pre-wrap">{{
            process.response
          }}</pre>
      </div>
    </div>
  </AppModal>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from "vue";
import AppModal from "@/components/ui/AppModal.vue";
import { dateFormat } from "@/commons/composables/utilities.ts";
import { api } from "@/app/services/api.service";
import { notify } from "@kyvg/vue3-notification";

interface Process {
  id: number;
  brn: string | null;
  createdAt: string | null;
  type: string | null;
  endPoint: string | null;
  payload: string | null;
  response: string | null;
  status: string;
  retries: number;
  updatedAt: string | null;
}

interface Pagination {
  totalElements: number;
  totalPages: number;
}

const showDetails = ref(false);
const loading = ref(false);
const loadingItem = ref(false);
const selectedIdx = ref<number | null>(null);
const currentPage = ref(1);
const limit = ref(12);
const pagination = reactive<Pagination>({ totalElements: 0, totalPages: 0 });
const statuses = ref<string | null>(null);
const process = reactive<Process>({
  id: 0,
  brn: null,
  createdAt: null,
  type: null,
  endPoint: null,
  payload: null,
  response: null,
  status: "",
  retries: 0,
  updatedAt: null
});
const search = ref("");
const startDate = ref<string | null>(null);
const endDate = ref<string | null>(null);
const stream = ref<Process[]>([]);

const statusPill = (status: string) => {
  const base = "text-xs px-2 py-0.5 rounded-full capitalize ";
  if (status === "Sent" || status === "Success") return base + "bg-green-50 text-green-700";
  if (status === "Failed" || status === "FAILED") return base + "bg-red-50 text-red-700";
  if (status === "IN_PROGRESS") return base + "bg-blue-50 text-blue-700";
  if (status === "PAUSED") return base + "bg-orange-50 text-orange-600";
  return base + "bg-gray-100 text-gray-600";
};

const errorToast = (title: string, text: string) => notify({ group: "foo", type: "error", title, text });
const successToast = (title: string, text: string) => notify({ group: "foo", type: "success", title, text });

const fetch = async () => {
  loading.value = true;
  const options = statuses.value ?? "PENDING,FAILED,PAUSED,IN_PROGRESS,SENT";
  try {
    let path = `/data-correction/reconciliations?status=${options}&page=${currentPage.value}&per_page=${limit.value}`;
    if (search.value.trim()) path += `&search=${encodeURIComponent(search.value.trim())}`;
    if (startDate.value) path += `&start_date=${startDate.value}`;
    if (endDate.value) path += `&end_date=${endDate.value}`;

    const response = await api().get(path);
    const data = response.data;
    stream.value = data.content || [];
    pagination.totalPages = data.totalPages || 0;
    pagination.totalElements = data.totalElements || 0;
  } catch (error) {
    stream.value = [];
    pagination.totalPages = 0;
    pagination.totalElements = 0;
    errorToast("Error", "Failed to load records");
  } finally {
    loading.value = false;
  }
};

const onSearch = () => {
  currentPage.value = 1;
  fetch();
};

const searchTerm = (term: string) => {
  search.value = term;
  onSearch();
};
const view = (details: Process) => {
  Object.assign(process, details);
  showDetails.value = true;
};

const formatPayload = (payload: string | null) => {
  if (!payload) return "";
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
};

const retry = async (details: Process, idx: number) => {
  selectedIdx.value = idx;
  loadingItem.value = true;
  try {
    await api().post(`/data-correction/reconciliations/reconcile/${details.id}`);
    loadingItem.value = false;
    successToast("Success", "Process returned to queue");
    fetch();
  } catch (error) {
    loadingItem.value = false;
    errorToast("Error", "Failed to retry. Try again later.");
  }
};

const next = () => {
  if (currentPage.value < pagination.totalPages) {
    currentPage.value++;
    fetch();
  }
};
const previous = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetch();
  }
};

onMounted(() => fetch());
</script>
